import React, { FunctionComponent, ReactNode } from 'react'
import { Button, ColorTypes, Heading, Text, useDate, Money as fMoney } from '@afterpaytouch/core'
import { Money } from '../Money'
import { PaymentArrangementSchedule, PaymentArrangementScheduleResponse } from '@afterpaytouch/portal-api/consumer/hardship/types'
import { daysTillDue, getFirstPayableItem } from '../../state/hardship/utils'
import { formatDueInDays } from '../../state/paymentSchedule/utils'
import { Table } from '../DataTable'
import { Trans, useTranslation } from 'next-i18next'
import { useGetPayableHardshipRepayments, useGetTotalPayableHardshipRepayments } from '../../state'
import { useFlag } from '../../hooks'
import { SupportedLocale } from '@afterpay/types'

const I18N_NAMESPACE = ['common', 'upcomingPayments']

interface UpcomingRepaymentScheduleProps {
  repaymentSchedule: PaymentArrangementScheduleResponse
  locale: SupportedLocale
  isDesktopSize: boolean
  onClick: (paymentschedule: PaymentArrangementSchedule, dueInDays: string) => void
}

interface UpcomingRepaymentDataCellProps {
  lineClamp?: boolean
  testNameSpace?: string
  bold?: boolean
  color?: ColorTypes
  children: ReactNode
}

const UpcomingRepaymentDataCell: FunctionComponent<UpcomingRepaymentDataCellProps> = ({ children, bold, color, lineClamp = false }) => {
  return (
    <Table.DataCell className='px-5'>
      <span className={'inline-block max-w-[200px]'}>
        <Text bold={bold} size={'M'} color={color} lineClamp={lineClamp ? 1 : undefined}>
          {children}
        </Text>
      </span>
    </Table.DataCell>
  )
}

export const UpcomingRepaymentSchedule: FunctionComponent<UpcomingRepaymentScheduleProps> = ({ repaymentSchedule, locale, isDesktopSize, onClick }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const hardshipPayNowEnabled = useFlag('consumer-portal-hardship-account-payments-enabled')
  const { formatUnixDate } = useDate({ locale, year: 'numeric', month: 'short', weekday: 'short' })
  const payableSchedules: PaymentArrangementSchedule[] = useGetPayableHardshipRepayments()
  const totalNumberOfPayments = useGetTotalPayableHardshipRepayments()
  const firstPayableItem: PaymentArrangementSchedule = getFirstPayableItem(repaymentSchedule)
  const getRepaymentNumber = (i: number): string => {
    return `${i + 1} of ${totalNumberOfPayments}`
  }
  const showPayButton = (currentScheduleId): boolean => firstPayableItem?.paymentScheduleUuid === currentScheduleId && hardshipPayNowEnabled
  const testNameSpace = 'upcoming-hardship-repayment'

  return (
    <>
      {isDesktopSize
        ? payableSchedules?.map((paymentSchedule, i) => {
            const daysUntilDue = daysTillDue(paymentSchedule?.overdueDatetime)
            const formatDays = t(formatDueInDays(daysUntilDue))
            const isOverDue = daysUntilDue < 0
            return (
              <React.Fragment key={`payment-arrangement-${paymentSchedule.paymentScheduleUuid}`}>
                <Table.Row testNameSpace={testNameSpace}>
                  <UpcomingRepaymentDataCell>
                    <div className={'my-5'}>
                      <Text>{getRepaymentNumber(i)}</Text>
                    </div>
                  </UpcomingRepaymentDataCell>
                  <UpcomingRepaymentDataCell>
                    <Text>{formatUnixDate(paymentSchedule?.overdueDatetime)}</Text>
                  </UpcomingRepaymentDataCell>
                  <UpcomingRepaymentDataCell>
                    <Text color={isOverDue ? 'Fire' : 'Black'} bold={isOverDue} testNameSpace={testNameSpace}>
                      <Trans values={{ count: daysUntilDue }} i18nKey={formatDays} />
                    </Text>
                  </UpcomingRepaymentDataCell>
                  <UpcomingRepaymentDataCell>
                    <span className={'inline-block max-w-[200px]'}>
                      <Money value={paymentSchedule.outstandingBalance} size='M' />
                    </span>
                  </UpcomingRepaymentDataCell>
                  <UpcomingRepaymentDataCell>
                    {showPayButton(paymentSchedule.paymentScheduleUuid) && (
                      <Button size='Regular' onClick={() => onClick(paymentSchedule, formatDays)} testNameSpace={testNameSpace}>
                        {t('upcomingPayments:activeUpcomingPayments:payNow')}
                      </Button>
                    )}
                  </UpcomingRepaymentDataCell>
                </Table.Row>
              </React.Fragment>
            )
          })
        : payableSchedules?.map((paymentSchedule, i) => {
            const daysUntilDue = daysTillDue(paymentSchedule?.overdueDatetime)
            const formatDays = t(formatDueInDays(daysUntilDue))
            const isOverDue = daysUntilDue < 0
            return (
              <div key={paymentSchedule.paymentScheduleUuid} data-testid={`${testNameSpace}-tableRow`}>
                <div className={'my-3 flex justify-between'}>
                  <div className='flex'>
                    <Text size='S'>{getRepaymentNumber(i)}</Text>
                    <div className={'ml-3 flex max-w-[150px] flex-col'}>
                      <Heading size='S' lineClamp={3}>
                        <Text bold size={'M'}>
                          {t('upcomingPayments:repaymentSchedule:repaymentPlan')}
                        </Text>
                      </Heading>
                      <Text size='S'>
                        <Text color={isOverDue ? 'Fire' : 'Black'} bold={isOverDue} testNameSpace={testNameSpace}>
                          <Trans values={{ count: daysUntilDue }} i18nKey={formatDays} />
                        </Text>
                      </Text>
                    </div>
                  </div>
                  <div className='flex h-10'>
                    {showPayButton(paymentSchedule.paymentScheduleUuid) && (
                      <Button size='Small' padding='Fixed' onClick={() => onClick(paymentSchedule, formatDays)} testNameSpace={testNameSpace}>
                        {t('upcomingPayments:activeUpcomingPayments:payButtonMobile', {
                          money: fMoney({ ...paymentSchedule.outstandingBalance, locale }),
                        })}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
    </>
  )
}
